/**
 * 閲覧記録(#13)。この品目のマニュアルと素材を、いつ誰が開いたか。
 *
 * 記録は配信プロキシが書く(viewer/server.mjs の recordAccess)。同じ人が同じ文書を
 * 5分以内に開き直した分は残らない。読めるのは管理者と料理長だけ(firestore.rules)。
 * 毎回読むと品目画面が重くなるので、押したときだけ読む。
 */
import { useState } from "react";
import { getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { useAuth } from "../auth";
import { useI18n } from "../lib/i18n";
import { describeAccess, type AccessLogDoc } from "../lib/access-log";
import type { UserLike } from "../lib/history";

const ACCESS_LOG_LIMIT = 100;

export function AccessLog({ dishId, users }: { dishId: string; users: Record<string, UserLike> }) {
  const { paths } = useAuth();
  const { t, locale } = useI18n();
  const [rows, setRows] = useState<(AccessLogDoc & { id: string })[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!paths) return;
    setBusy(true);
    setError(null);
    try {
      const snap = await getDocs(
        query(
          paths.accessLogs(),
          where("targetId", "==", dishId),
          orderBy("at", "desc"),
          limit(ACCESS_LOG_LIMIT),
        ),
      );
      setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as AccessLogDoc) })));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const who = (r: AccessLogDoc) => users[r.uid]?.displayName || users[r.uid]?.email || r.email || r.uid;

  return (
    <div className="card">
      <h2>{t("閲覧記録")}</h2>
      <p className="muted small">
        {t("この品目のマニュアルと素材を、いつ誰が開いたかの記録です（新しい順に最大{n}件。同じ人が5分以内に開き直した分は残りません）。", {
          n: ACCESS_LOG_LIMIT,
        })}
      </p>
      {rows == null ? (
        <button className="link" disabled={busy} onClick={() => void load()}>
          {busy ? t("読み込み中…") : t("閲覧記録を見る")}
        </button>
      ) : rows.length === 0 ? (
        <p className="muted small">{t("まだ閲覧の記録はありません。")}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t("日時")}</th>
              <th>{t("閲覧者")}</th>
              <th>{t("見たもの")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const d = describeAccess(r.kind, r.path);
              return (
                <tr key={r.id}>
                  <td className="muted small" data-label={t("日時")}>
                    {r.at ? r.at.toDate().toLocaleString(locale) : "—"}
                  </td>
                  <td className="small" data-label={t("閲覧者")}>{who(r)}</td>
                  <td className="small" data-label={t("見たもの")}>
                    {t(d.label)} <span className="muted">{d.file}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {error && <p className="error small">{error}</p>}
    </div>
  );
}
